import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, HelpCircle, Check, AlertTriangle, Loader2, Lock } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import { formatErrorDetail } from '../utils/formatError';

const PATHWAY_CONTENT = {
  A: {
    heading: 'Pathway A: Founders Track',
    body: 'You are ready to build. Your assessment confirms you have the mindset, financial readiness, and circumstances to pursue building a business right now. This is not a compliment — it is a specific finding based on how you answered.',
    nextSteps: [
      'Stage 2: Idea Generation begins immediately on this platform',
      'You will explore, generate, evaluate and select 1-2 business ideas worth pursuing',
      'Brian is here to support you throughout — click the help icon any time',
      'Most people complete Stage 2 in 4-8 weeks at their own pace',
    ],
    nextLabel: 'What happens next:',
    cta: 'Begin Stage 2: Idea Generation',
    accentBg: '#dcfce7',
    accentText: '#166534',
    accentBorder: '#86efac',
  },
  B: {
    heading: 'Pathway B: Employment Track',
    body: "A strategic career move is where you will genuinely thrive right now. This is not a rejection. Your assessment showed that right now — your circumstances, your motivations, and where you are in life — point strongly toward a different kind of success. The door stays open. If your circumstances change, you can return to Stage 1 at any time and reassess.",
    nextSteps: [
      'Career positioning — articulating your professional value clearly',
      'Role targeting — finding the right roles, companies, and industries for your profile',
      'CV and LinkedIn optimisation — built to get past AI screening',
      'Interview preparation — coached and practised',
      'Job search strategy — targeted, not scattergun',
    ],
    nextLabel: 'What Pathway B gives you:',
    cta: 'Begin Stage 2: Employment Track',
    accentBg: '#dbeafe',
    accentText: '#1e40af',
    accentBorder: '#93c5fd',
    brianNote: "If this result feels surprising or unfair, I understand. I'm here to help you make sense of it. Click me and let's talk through what the assessment found — and what your options are from here.",
  },
  C: {
    heading: 'Pathway C: Mindset Discovery',
    body: 'You have real potential. This is not the finish line — it is the right starting point. Your assessment showed genuine business interest, domain expertise, and resilience. What it also showed is that there are 1-2 specific foundations to build before you are fully ready to launch. Pathway C is a deliberate development track — not a waiting room.',
    nextSteps: [
      'Stage 2: Idea Generation — same as Pathway A, with additional mindset support built in',
      'Ongoing confidence and momentum check-ins throughout',
      'Reassessment built into Stage 2 completion — pathways can change',
      'Brian is here whenever you need a nudge or a reality check',
    ],
    nextLabel: 'What Pathway C includes:',
    cta: 'Begin Stage 2: Idea Generation',
    accentBg: '#fef9c3',
    accentText: '#854d0e',
    accentBorder: '#fde047',
    closingLine: "Right now is not never. It is not yet — and 'not yet' has a path forward.",
  },
};

const TAGLINES = {
  A_green: 'Ready \u2022 Capable \u2022 Go',
  A_yellow: 'Ready \u2022 Conditions Apply \u2022 Go Thoughtfully',
  B: 'Talented \u2022 Structured \u2022 Built to Lead',
  C: 'Capable \u2022 Developing \u2022 Ready-with-Support',
};

// Build personalised insights from assessment data
const buildInsights = (results) => {
  const insights = [];
  const dims = results.dimensions || {};

  // Positive signals (green checks)
  if ((dims.resilience?.score || 0) >= 7) {
    insights.push({ type: 'positive', text: 'Your resilience score is one of the strongest we see — your failure story showed genuine ownership' });
  }
  if ((dims.idea_fit?.score || 0) >= 7) {
    insights.push({ type: 'positive', text: 'You have a specific, well-formed idea backed by genuine domain expertise' });
  }
  if ((dims.mindset?.score || 0) >= 7) {
    insights.push({ type: 'positive', text: 'Your motivation is clear and specific — you know what you want to build and why' });
  }
  if ((dims.financial?.score || 0) >= 7) {
    insights.push({ type: 'positive', text: 'Your financial position gives you real runway to build without reckless pressure' });
  }
  if ((dims.life?.score || 0) >= 7) {
    insights.push({ type: 'positive', text: 'Your life circumstances are stable and supportive of this kind of change' });
  }

  // Areas to develop (amber diamonds)
  if ((dims.mindset?.score || 0) < 7 && (dims.mindset?.score || 0) >= 4) {
    insights.push({ type: 'develop', text: 'Your motivation is a genuine mix right now — part escape, part real interest. Both are valid, but the mix matters' });
  }
  if ((dims.idea_fit?.score || 0) < 5) {
    insights.push({ type: 'develop', text: 'Your idea is still forming — Stage 2 is specifically designed for this' });
  }
  if ((dims.financial?.score || 0) < 5) {
    insights.push({ type: 'develop', text: 'Your financial runway is tight — building toward revenue matters more for your situation' });
  }
  if (results.text_analysis?.q22 === 'low') {
    insights.push({ type: 'develop', text: 'You have not yet spoken to potential customers about this idea — that is the most important gap to close' });
  }

  return insights;
};

const PathwayAssignment = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBrian, setShowBrian] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const res = await axios.get(`${API}/assessment/stage1/results`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setResults(res.data.results);
      } catch (e) {
        navigate('/results');
      }
      setLoading(false);
    };
    fetchResults();
  }, [token, navigate]);

  if (loading || !results) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#94a3b8' }} />
      </div>
    );
  }

  const pathway = results.pathway;
  const content = PATHWAY_CONTENT[pathway] || PATHWAY_CONTENT.C;
  const firstName = (user?.name || 'there').split(' ')[0];
  const insights = buildInsights(results);

  const taglineKey = pathway === 'A' ? (results.light_colour === 'green' ? 'A_green' : 'A_yellow') : pathway;
  const tagline = TAGLINES[taglineKey] || TAGLINES.C;

  const stagesUnlocked = user?.stages_unlocked;

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/payments/create-checkout`, {
        package_id: 'stages_2_9',
        origin_url: origin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <header style={{ padding: '16px 24px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '22px', fontWeight: 800, color: '#1E491A', letterSpacing: '-0.5px' }}>SPRING.</span>
        <span style={{ fontSize: '12px', color: '#94a3b8' }}>Your Pathway</span>
      </header>

      <main style={{ maxWidth: '640px', margin: '0 auto', padding: '40px 24px 80px' }}>
        {/* Pathway badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          background: content.accentBg, color: content.accentText,
          border: `1px solid ${content.accentBorder}`,
          padding: '6px 16px', borderRadius: 0, fontSize: '12px', fontWeight: 700,
          letterSpacing: '0.5px', marginBottom: '16px', textTransform: 'uppercase',
        }} data-testid="pathway-badge">
          Pathway {pathway}
        </div>

        {/* Heading */}
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#1E491A', letterSpacing: '-0.5px', marginBottom: '16px', lineHeight: 1.2 }} data-testid="pathway-heading">
          {content.heading}
        </h1>

        {/* Tagline */}
        <p style={{ fontSize: '14px', color: content.accentText, fontWeight: 600, fontStyle: 'italic', marginBottom: '24px' }} data-testid="pathway-tagline">
          {tagline}
        </p>

        {/* Body copy */}
        <div style={{
          background: '#fff', borderRadius: '20px', padding: '28px',
          border: '1px solid #e2e8f0', marginBottom: '24px', lineHeight: 1.7,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }} data-testid="pathway-body">
          <p style={{ fontSize: '15px', color: '#334155' }}>{content.body}</p>
        </div>

        {/* Personalised insights (for C pathway, also shown for A & B if available) */}
        {insights.length > 0 && (
          <div style={{
            background: '#fff', borderRadius: '20px', padding: '24px',
            border: '1px solid #e2e8f0', marginBottom: '24px',
          }} data-testid="pathway-insights">
            <p style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', marginBottom: '16px' }}>
              Here is what your assessment found:
            </p>
            {insights.map((ins, i) => (
              <div key={i} style={{
                display: 'flex', gap: '12px', alignItems: 'flex-start',
                marginBottom: '12px',
              }}>
                {ins.type === 'positive' ? (
                  <Check style={{ width: 16, height: 16, color: '#22c55e', flexShrink: 0, marginTop: '2px' }} />
                ) : (
                  <AlertTriangle style={{ width: 16, height: 16, color: '#eab308', flexShrink: 0, marginTop: '2px' }} />
                )}
                <p style={{ fontSize: '14px', color: '#334155', lineHeight: 1.6, margin: 0 }}>{ins.text}</p>
              </div>
            ))}
          </div>
        )}

        {/* What happens next */}
        <div style={{
          background: '#fff', borderRadius: '20px', padding: '24px',
          border: '1px solid #e2e8f0', marginBottom: '24px',
        }}>
          <p style={{ fontSize: '14px', fontWeight: 700, color: '#1E491A', marginBottom: '14px' }}>
            {content.nextLabel}
          </p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {content.nextSteps.map((step, i) => (
              <li key={i} style={{
                display: 'flex', gap: '10px', alignItems: 'flex-start',
                fontSize: '14px', color: '#475569', lineHeight: 1.6,
                marginBottom: '10px',
              }}>
                <span style={{ color: content.accentText, fontWeight: 700, flexShrink: 0 }}>→</span>
                {step}
              </li>
            ))}
          </ul>
        </div>

        {/* Closing line (Pathway C) */}
        {content.closingLine && (
          <p style={{
            fontSize: '15px', color: '#334155', fontStyle: 'italic',
            textAlign: 'center', marginBottom: '24px', lineHeight: 1.6,
          }}>
            {content.closingLine}
          </p>
        )}

        {/* Brian block (Pathway B) */}
        {content.brianNote && (
          <div style={{
            background: '#f1f5f9', borderRadius: 0, padding: '20px',
            borderLeft: '3px solid #1a1a1a', marginBottom: '24px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <HelpCircle style={{ width: 14, height: 14, color: '#64748b' }} />
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>BRIAN</span>
            </div>
            <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.7 }}>{content.brianNote}</p>
          </div>
        )}

        {/* CTAs */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/badge')}
            data-testid="pathway-continue-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '10px',
              background: '#1a1a1a', color: '#F6ADBC', border: 'none',
              padding: '16px 36px', borderRadius: 0,
              fontSize: '16px', fontWeight: 700, cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
            onMouseLeave={(e) => { e.target.style.transform = ''; e.target.style.boxShadow = ''; }}
          >
            View Your Badge
            <ArrowRight style={{ width: 18, height: 18 }} />
          </button>

          {!stagesUnlocked && (
            <>
              <button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                data-testid="pathway-unlock-stages-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '10px',
                  background: '#F6ADBC', color: '#1E491A', border: 'none',
                  padding: '16px 36px', borderRadius: 0,
                  fontSize: '16px', fontWeight: 700, cursor: checkoutLoading ? 'wait' : 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => { if (!checkoutLoading) { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)'; } }}
                onMouseLeave={(e) => { e.target.style.transform = ''; e.target.style.boxShadow = ''; }}
              >
                {checkoutLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Lock style={{ width: 16, height: 16 }} />
                    Unlock Stages 2–9 — $2,500 NZD
                  </>
                )}
              </button>
              <button
                onClick={async () => {
                  try {
                    const ax = (await import('axios')).default;
                    await ax.post(`${API}/payments/demo-bypass`, { package_id: 'stages_2_9' }, { headers: { Authorization: `Bearer ${token}` } });
                    window.location.reload();
                  } catch {}
                }}
                data-testid="pathway-demo-bypass-btn"
                style={{
                  background: 'none', border: '1px solid #ddd', color: '#999',
                  padding: '10px 20px', borderRadius: 0,
                  fontSize: '13px', cursor: 'pointer',
                }}
              >
                Skip Payment (Demo)
              </button>
            </>
          )}

          {stagesUnlocked && (
            <button
              onClick={() => navigate('/portal/stages')}
              data-testid="pathway-go-stages-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
                padding: '14px 32px', borderRadius: 0,
                fontSize: '15px', fontWeight: 600, cursor: 'pointer',
              }}
            >
              <Check style={{ width: 16, height: 16 }} />
              Stages Unlocked — Go to Stage 2
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default PathwayAssignment;
